<script lang="ts">
 /**
 * Bottom bar housing the capture mode picker.
 *
 * @remarks
 * Dark chrome with safe-area inset padding for phones with home
 * indicators (uses `env(safe-area-inset-bottom)`). Hidden during
 * drawer interactions (photo strip, processing, suggestion) and
 * when the container picker is open.
 *
 * Contains only the {@link CaptureModePicker} — kept as a separate
 * component so the mode picker can be reused or repositioned without
 * carrying the bar's safe-area padding.
 */
</script>

<script setup lang="ts">
import type { CaptureMode } from "@/composables/useCaptureFlow"

import CaptureModePicker from "./CaptureModePicker.vue"

defineProps<{
  /** The currently active capture mode ("items" or "container"). */
  mode: CaptureMode
}>()

defineEmits<{
  /** Fired when the user taps a mode option. Parent updates the flow's mode ref. */
  "update:mode": [value: CaptureMode]
}>()
</script>

<!-- Dark chrome: matches camera app convention for reduced glare. -->
<template>
  <div
    class="flex justify-center bg-black px-4 pt-3 pb-3"
    :style="{ paddingBottom: 'max(0.75rem, var(--safe-bottom))' }"
  >
    <CaptureModePicker :mode="mode" @update:mode="$emit('update:mode', $event)" />
  </div>
</template>
